export default {
  app: {
    privacy: {
      policy: 'privacy policy',
      expansive: 'By using Expansive, you agree to the Expansive {action}',
      client: '@:app.privacy.expansive{lineBreak}& {clientName} {clientAction}',
    },
    sessionExpired: 'Session expired',
    sessionExpiredDescription:
      'Your session has expired. Please log in again to continue using the application.',
  },
  components: {
    appContainer: {
      chooseLanguage: 'Choose language',
      chooseLanguageDescription:
        'Choose your preferred language for the application. This will change the language of the interface and content.',
    },
    userSelection: {
      cancel: 'Cancel',
      createNewUser: "Their name isn't listed",
      newUser: 'New user',
      selectAnExistingUser: 'Select an existing user',
      selectUserHint: 'Select an existing user or create a new one to continue.',
      startTypingToSearch: 'Start typing...',
    },
    verification: {
      codeVerification: {
        continue: 'Continue',
        header: 'Enter site access code',
        invalidCode: 'Invalid code, please try again.',
      },
      geoVerification: {
        continueWithoutVerifying: 'Continue without verifying location',
        enterSiteAccessCode: 'Enter site access code',
        locationNotEnabledOrAvailable: 'Why am I seeing this?',
        notAvailable: {
          description: 'GPS location is not supported on this device.',
          header: 'Location not supported',
        },
        notEnabled: {
          description: 'Location services are not enabled or permission has not been granted.',
          header: 'Location not enabled',
        },
        notVerified: {
          description: 'GPS indicates that you are not on site.',
          header: 'Location not verified',
        },
        tryAgain: 'Try again',
        unavailable: {
          description: 'We could not verify your GPS location.',
          header: 'Location unavailable',
        },
        unknown: {
          description: 'We are unable to determine your location.',
          header: 'Location unknown',
        },
        verified: {
          description: 'GPS indicates that you are on site.',
          header: 'Location verified',
        },
      },
    },
    workOrder: {
      assets: {
        emptyTitle: 'No assets found',
        emptyDescription: 'There are no assets associated with this work order.',
        warrantyParts: 'Parts warranty expires',
        serialNumber: 'Serial number',
        none: '<none>',
      },
      updates: {
        emptyTitle: 'No updates found',
        emptyDescription: 'There are no updates for this work order.',
        unknownUser: 'Unknown user',
        none: 'None',
        addComment: {
          buttonLabel: 'Add comment',
        },
      },
      startWorkCheck: {
        startWorkCheckList: {
          hazards: 'Hazards',
          no: 'No',
          yes: 'Yes',
        },
      },
    },
    attachmentsList: {
      attachments: 'Attachments',
      documents: 'Documents',
      siteDocuments: 'Site Documents',
    },
  },
  stores: {
    authenticated: {
      startWorkCheckStore: {
        hazards: 'Hazards',
        induction: 'Induction',
        safeToProceed: 'Safe to proceed',
      },
    },
  },
  views: {
    authenticated: {
      sessions: {
        dueBy: 'Due by date',
        activeSessions: 'Active sessions',
        activeSessionsDescription:
          "You're already checked into the following work orders. You must checkout of these work orders before checking into a new work order.",
        resumeSession: 'Resume',
        startTime: 'Started at',
        unexpectedErrorTitle: 'Unexpected error',
        unexpectedErrorMessage:
          'An unexpected error occurred while trying to create your sessions. Please try again later.',
        engineerNotFound: 'Engineer not found',
        engineerNotFoundDescription:
          'The engineer you are trying to check in is not found in the system. Please contact your administrator.',
      },
      verification: {
        code: {
          description:
            'To check in, please obtain the 4-digit code from the site manager or designated contact.',
        },
        geo: {
          continue: 'Continue',
        },
      },
      workOrders: {
        checkout: {
          index: {
            signOff:
              'Please obtain the access code from the site manager or designated contact to complete your visit.',
            checkOut: 'Check out',
            completeBeforeLeaving: 'Please complete the following form before leaving.',
            continue: 'Continue',
            noResolutionSelected: 'Please select a resolution before continuing.',
            quoteRequired: {
              hint: 'Visit complete, quote to follow',
              label: 'Quote required',
            },
            returnVisitRequired: {
              hint: "We'll be returning again to complete",
              label: 'Return visit required',
            },
            serviceCompleted: {
              hint: 'No further action required',
              label: 'Service completed',
            },
            uploadCheckout: 'Upload a file, photo, or video',
            visitNotes: 'Visit notes',
            workOrderResolution: 'Work order resolution',
            enterNotes: 'Enter any notes here...',
            errors: {
              timedOut: 'Time out, exceeded 30 seconds. Please try again.',
              closingSession: 'Error closing session',
            },
          },
          success: {
            description:
              'Thank you for visiting {site}. | {site} still has 1 remaining open work order. | {site} still has {count} remaining open work orders.',
            endVisit: 'End visit',
            header: 'You have successfully checked out',
            viewWorkOrders: 'Check into another work order',
          },
          verification: {
            code: {
              description:
                'To check out, please obtain the 4-digit code from the site manager or designated contact.',
              continue: 'Continue',
            },
            geo: {
              checkOut: 'Check out',
              continue: 'Continue',
            },
          },
        },
        detail: {
          assets: 'Assets',
          call: 'Call',
          checkOut: 'Check out',
          closed: 'Closed',
          details: 'Details',
          dueBy: 'Due by date',
          manageEngineers: 'Manage engineers',
          notAvailable: 'Not available',
          pointOfContact: 'Point of contact',
          siteAccessTimes: 'Site access times',
          siteAccessInstructions: 'Access Instructions',
          siteAddress: 'Site address',
          siteNotes: 'Site notes',
          siteTelephone: 'Site telephone',
          siteLocation: 'Site location',
          updates: 'Updates',
          more: 'More',
          mismatchErrorTitle: 'Work order error',
          mismatchErrorMessage:
            'The work order you are trying to access does not match the one you are currently checked into. Please check your work order and try again.',
          siteDocuments: 'Site documents',
          validFrom: 'Valid from',
          validUntil: 'Valid until',
          documentName: 'Document name',
          documentType: 'Document type',
          attachments: 'Work order files',
          download: 'Download',
          workDetails: 'Work details',
        },
        engineers: {
          assistants: {
            addAssistantEngineer: 'Add assistant engineer',
            assistantEngineers: 'Assistant engineers',
            description: 'For security reasons, please specify the names of each engineer on site.',
            editMainEngineer: 'Edit main engineer',
            errors: {
              creatingEngineer: 'Error creating engineer',
              creatingSession: 'Error creating session',
            },
            header: 'Engineers on site',
            mainEngineer: 'Main engineer',
            newAssistantEngineer: 'Enter their full name',
            next: 'Next',
            removeEngineer: 'Remove engineer',
            selectExistingAssistantEngineer: 'Start typing their name',
          },
          index: {
            description: 'For security reasons, please specify the names of each engineer on site.',
            errorCreatingEngineer: 'Error creating engineer, please try again.',
            header: 'Engineers on site',
            hintMainEngineer: "Enter main engineer's full name",
            newMainEngineer: 'New main engineer',
            next: 'Next',
            selectExistingMainEngineer: 'Select an existing main engineer',
            yes: 'Yes',
            no: 'No',
            mainEngineerLabel: 'Are you the main engineer?',
          },
        },
        index: {
          callUsOn: 'Call us on {phoneNumber}',
          description: "Can't find what you're looking for?",
          dueBy: 'Due by date',
          header: 'Select work order',
          notAvailable: 'Not available',
          searchWorkOrders: 'Search work orders...',
        },
        manageEngineers: {
          manageEngineers: 'Manage engineers',
          notWorking: 'Inactive workers',
          pause: 'Pause',
          resume: 'Resume',
          workingNow: 'Active workers',
          startTime: 'Start time',
          endTime: 'End time',
          duration: 'Duration',
        },
        startWorkChecks: {
          feedback: {
            feedbackTitle: 'Feedback',
            feedbackDescription:
              'Please provide feedback on the work order to help us improve our services.',
            feedbackLabel: 'Feedback',
            filesLabel: 'Attachments',
            submissionLabel: 'Submit feedback',
          },
          hazards: {
            back: 'Go back',
            hazards: 'Hazards',
            next: 'Next',
            no: 'No',
            noTasksForHazard: 'There are no tasks to complete for this hazard.',
            yes: 'Yes',
          },
          induction: {
            continue: 'Continue',
            induction: 'Induction',
          },
          index: {
            checkIn: 'Check in',
            description: 'Please complete the following risk assessment for the selected work.',
            fail: 'Failed processing start work order checks.',
            haltMessage:
              'Potential risks detected. Please halt work, review safety guidelines, and contact your supervisor for further instructions.',
            header: 'Start work checks',
            endVisit: 'End visit',
            leaveFeedback: 'Leave feedback',
          },
          safeToProceed: {
            continue: 'Continue',
            safeToProceed: 'Safe to proceed',
          },
        },
      },
    },
    index: {
      title: 'Welcome',
      description: 'Scan the QR code at your location to get started.',
    },
    login: {
      errorTitle: 'Unexpected error',
      errorMessage: 'An unexpected error occurred. Please try scanning the QR code again.',
    },
    unauthenticated: {
      welcome: {
        title: 'Welcome to {site}',
        description:
          'Before we can show you any work orders, we need to verify your details. Click the button below to continue.',
        next: 'Continue',
      },
      index: {
        errorTitle: 'Unexpected error',
        errorMessage: 'An unexpected error occurred. Please try scanning the QR code again.',
      },
      contractor: {
        title: 'Who do you work for?',
        contractor: 'Contractor',
        contractorLabel: 'Start typing your company name',
        contractorHint: 'You must enter 3 characters or more to see results',
        defaultSelectPlaceholder: '--Please select--',
        next: 'Next',
      },
      user: {
        title: 'Who are you?',
        cancel: 'Cancel',
        createNewUserLabel: "My name isn't listed",
        existingUserLabel: 'Start typing your name',
        existingUserHint: 'You must enter 3 characters or more to see results',
        newUserLabel: 'Enter your full name',
        next: 'Next',
        user: 'User',
      },
      verification: {
        methods: {
          add: {
            add: 'Add verification method',
            defaultSelectPlaceholder: '--Please select--',
            emailLabel: 'Email',
            existingMethodWarning:
              'You already have a pending request for this verification method awaiting admin approval. Adding a new one will override it.',
            method: 'Method',
            methodLabel: 'Method',
            mobileCountryHint: 'Please select your country code',
            mobileCountryLabel: 'Mobile country code',
            mobileHint: 'Please enter your mobile number',
            mobileLabel: 'Mobile',
            title: 'Add verification method',
          },
          confirm: {
            confirm: 'Confirm',
            description: 'Please confirm your verification method to continue.',
            label: 'Enter your code',
            resend: 'Resend',
            resendPrompt: "Didn't receive a code?",
            title: 'Confirm Verification Method',
          },
          confirmed: {
            description:
              "You'll be notified once this verification method has been approved by your admin, and you can then start using it to verify your identity.",
            forceApproval: "Until it's approved, you are unable to access work orders.",
            nonForceApproval:
              'In the meantime, you can continue to access work orders with reduced access.',
            nonForceApprovalButtonLabel: 'Continue with reduced access',
            title: 'Awaiting approval',
          },
          addNew: 'Add new verification method',
          description:
            'We need to verify your identity in order to give you full access to the work order.',
          email: 'Email',
          hint: "We'll send a link to your selected verification method which gives you full access to the work order. If you need to change any existing verification methods, please contact your administrator.",
          label: 'Select a verification method',
          next: 'Next',
          pendingHint: 'Awaiting approval from your admin',
          sms: 'SMS',
          title: 'Verification',
        },
        optional: {
          hint: 'Verifying your user details will give you full access to the work order',
          label: 'Do you want to verify your user details?',
          next: 'Next',
          no: 'No',
          noHint:
            "I don't want to verify my user details and continue with reduced access to the work order",
          title: 'Verification',
          yes: 'Yes',
          yesHint: 'I want to verify my user details and get full access to the work order',
        },
        sent: {
          description:
            'A login link has been sent to {identifier}. Please click the link to complete your login.',
          title: 'Login link sent',
        },
      },
    },
  },
  utils: {
    getActivityLog: {
      yesterday: 'Yesterday',
      today: 'Today',
      days: {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
      },
      months: {
        january: 'January',
        february: 'February',
        march: 'March',
        april: 'April',
        may: 'May',
        june: 'June',
        july: 'July',
        august: 'August',
        september: 'September',
        october: 'October',
        november: 'November',
        december: 'December',
      },
    },
  },
  composables: {
    useRedirectToError: {
      title: 'Something went wrong',
      message: 'Please try scanning the QR code again.',
    },
    useResolveWorkOrderEvents: {
      user: {
        unknown: 'Unknown user',
      },
      none: 'None',
      types: {
        stateTransition: 'Status changed',
        contractorAssigned: 'Contractor assigned',
        engineerAssigned: 'Engineer assigned',
        serviceAssigned: 'Service assigned',
        priorityChanged: 'Priority changed',
        actionableDateChanged: 'Actionable date changed',
        dueDateChanged: 'Due date changed',
        etaDateChanged: 'Estimated time of arrival date changed',
        spendLimitApproved: 'Spend limit approved',
        spendLimitCancelled: 'Spend limit request cancelled',
        spendLimitRejected: 'Spend limit rejected',
        spendLimitRequested: 'Spend limit requested',
        jobSheetUploaded: 'Job sheet uploaded',
        slaAlertSent: 'Time to attend breached SLA alert',
        issuedWorkPermit: 'Work permit issued',
        requestForServiceSent: 'Request for service sent',
        comment: 'Comment added',
        unknown: 'Unknown event',
      },
      labels: {
        actionableDate: 'Actionable date',
        comment: 'Comment',
        file: 'File',
        contractor: 'Contractor',
        dueDate: 'Due date',
        etaDate: 'Estimated time of arrival date',
        engineer: 'Engineer',
        priority: 'Priority',
        requestForService: 'Request for service',
        service: 'Service',
        spendLimit: 'Spend limit',
        status: 'Status',
        workPermit: 'Work permit',
      },
    },
  },
}
